/* ═══════════════════════════════════════════════════════════════════════════
   La sauvegarde.

   `localStorage` est écarté et ce n'est pas un détail : il est SYNCHRONE, donc
   une écriture en cours de partie bloque le thread principal et coûte une
   frame. IndexedDB (via idb-keyval) est asynchrone — on peut écrire pendant que
   le jeu tourne sans que personne ne le sente.

   Trois choses qu'on ne peut pas rétro-installer et qui sont donc ici dès le
   premier jour :

     1. UN NUMÉRO DE VERSION ET DES MIGRATIONS. Le jour où la structure change,
        les sauvegardes existantes doivent survivre. Sans versionnage, la seule
        option restante est de les effacer — c'est-à-dire de perdre la
        progression de tous les joueurs.

     2. `navigator.storage.persist()`. Sans lui, le navigateur peut évacuer les
        données sous pression disque. Ce n'est pas une garantie, mais c'est le
        seul geste qui compte.

     3. L'EXPORT/IMPORT JSON. C'est le seul vrai filet : la purge ITP de Safari
        efface le stockage d'origine après sept jours d'inactivité, et aucune
        API ne l'en empêche. Un joueur doit pouvoir emporter sa sauvegarde.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Interface minimale d'un magasin clé/valeur — permet d'éprouver les
 *  migrations sans IndexedDB, donc sans navigateur. */
export interface KeyValueStore {
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
  del(key: string): Promise<void>;
}

import { DEFAULT_BOMB_ID } from '../content/bombs';
import { DEFAULT_SHIP_ID, DEFAULT_WEAPON_ID } from '../sim/defs';
import { DEFAULT_SECONDARY_ID } from '../sim/secondary';

export const SAVE_KEY = 'neon-descent:save';
export const SAVE_VERSION = 7;

/** Forme actuelle de la sauvegarde. */
export interface SaveData {
  version: number;
  /** ⚠️ Anciennement `alloy`. Les CRÉDITS sont la monnaie persistante gagnée au
   *  score : renommer plutôt qu'ajouter était indispensable, car conserver les
   *  deux aurait créé une quatrième monnaie sans lui donner de rôle — soit
   *  exactement l'illisibilité que le plan met en garde (§6.9). */
  credits: number;
  bestScore: number;
  /** Meilleur score PAR PALIER de difficulté, indexé comme `Difficulty`.
   *
   *  ⚠️ Un record unique toutes difficultés confondues aurait poussé à ne jouer
   *  qu'au palier le plus généreux pour le battre, ce qui vide les deux autres
   *  de leur sens. `bestScore` reste le maximum global — c'est lui qu'affiche
   *  l'écran-titre, parce qu'un joueur veut y voir son meilleur souvenir, pas
   *  un tableau. */
  bestByDifficulty: number[];
  /** Dernier palier choisi, repris par défaut au briefing suivant. */
  lastDifficulty: number;
  /** Arme secondaire emportée. Vaut l'identifiant du catalogue, jamais un
   *  index : les index se décalent dès qu'un fichier est ajouté, et une
   *  sauvegarde survit aux versions. */
  secondaryId: string;
  /** Ennemis déjà croisés en jeu. Alimente le Journal de bord, qui ne montre
   *  QUE ce qui a été rencontré : un bestiaire complet dès la première partie
   *  divulguerait les trois mondes, boss compris, et annulerait les cinq
   *  secondes de silence qui les mettent en scène. */
  seenEnemies: string[];
  /** Équipement emporté. Des IDENTIFIANTS et jamais des index : le registre
   *  numérote par ordre alphabétique de fichier, si bien qu'ajouter un contenu
   *  décalerait silencieusement tout ce qui le suit — y compris dans une
   *  sauvegarde écrite par une version antérieure. */
  shipId: string;
  weaponId: string;
  bombId: string;
  /** Contraintes volontaires activées, par `grants`. */
  constraintIds: string[];
  runsPlayed: number;
  /** Nœuds de méta-progression achetés. */
  unlocked: string[];
  /** Jalons de maîtrise décrochés. Ce sont EUX qui produisent les points de
   *  compétence : les points ne sont jamais stockés, ils se recalculent depuis
   *  cette liste moins ce qui a été dépensé. Un solde stocké finirait par
   *  diverger de sa propre justification. */
  milestones: string[];
  options: {
    shake: number;
    flash: number;
    master: number;
    music: number;
    sfx: number;
    /** Mode de daltonisme (valeur de `Colorblind`, 0 = aucun). */
    colorblind: number;
    /** Vitesse de jeu (0,5 à 1,5 ; 1 = normale). */
    gameSpeed: number;
  };
}

export function defaultSave(): SaveData {
  return {
    version: SAVE_VERSION,
    credits: 0,
    bestScore: 0,
    bestByDifficulty: [0, 0, 0],
    lastDifficulty: 1,
    secondaryId: DEFAULT_SECONDARY_ID,
    seenEnemies: [],
    shipId: DEFAULT_SHIP_ID,
    weaponId: DEFAULT_WEAPON_ID,
    bombId: DEFAULT_BOMB_ID,
    constraintIds: [],
    runsPlayed: 0,
    unlocked: [],
    milestones: [],
    // ⚠️ `colorblind` et `gameSpeed` sont fusionnés dans TOUTE sauvegarde chargée
    // via `migrate()` (`{ ...base.options, ...data.options }`) : une sauvegarde
    // antérieure qui ne les contient pas hérite donc de ces valeurs neutres, sans
    // migration dédiée à écrire.
    options: { shake: 1, flash: 1, master: 0.9, music: 0.6, sfx: 1, colorblind: 0, gameSpeed: 1 },
  };
}

/* ── Migrations ───────────────────────────────────────────────────────────
   Une fonction par saut de version, appliquées en chaîne. Écrire des
   migrations avant d'en avoir besoin peut sembler prématuré ; c'est en réalité
   le seul moment où c'est facile, parce qu'aucune donnée réelle n'existe encore
   et qu'on peut se tromper sans conséquence. */
type Migration = (data: Record<string, unknown>) => Record<string, unknown>;

const MIGRATIONS: Readonly<Record<number, Migration>> = {
  // v1 → v2 : les options audio et les sliders d'accessibilité sont apparus.
  // Une sauvegarde v1 ne les contient pas : on complète sans rien écraser.
  1: (d) => ({
    ...d,
    version: 2,
    options: {
      shake: 1,
      flash: 1,
      master: 0.9,
      music: 0.6,
      sfx: 1,
      ...((d['options'] as object) ?? {}),
    },
  }),

  // v2 → v3 : l'alliage devient les CRÉDITS, et les jalons de maîtrise
  // apparaissent. Le renommage doit préserver le solde — un joueur qui perdrait
  // sa monnaie à la mise à jour n'aurait aucune raison de revenir.
  2: (d) => {
    const { alloy, ...rest } = d as Record<string, unknown> & { alloy?: unknown };
    return {
      ...rest,
      version: 3,
      credits: typeof alloy === 'number' ? alloy : (d['credits'] ?? 0),
      milestones: Array.isArray(d['milestones']) ? d['milestones'] : [],
    };
  },

  /* v3 → v4 : les trois difficultés apparaissent.
     Le meilleur score existant est reporté sur le palier OPÉRATEUR, et pas
     réparti ni dupliqué : c'est le seul palier qui existait, donc le seul où ce
     score a été obtenu. L'attribuer à Vétéran offrirait un record imaginaire, et
     le jeter punirait le joueur d'avoir été là avant la fonctionnalité. */
  /* v6 → v7 : vaisseaux, armes primaires, bombes et contraintes deviennent
     choisissables. Une sauvegarde antérieure n'a rien choisi : on pose le
     matériel de série et aucune contrainte, c'est-à-dire exactement ce avec
     quoi elle a été jouée. */
  6: (d) => ({
    ...d,
    version: 7,
    shipId: typeof d['shipId'] === 'string' ? d['shipId'] : DEFAULT_SHIP_ID,
    weaponId: typeof d['weaponId'] === 'string' ? d['weaponId'] : DEFAULT_WEAPON_ID,
    bombId: typeof d['bombId'] === 'string' ? d['bombId'] : DEFAULT_BOMB_ID,
    constraintIds: Array.isArray(d['constraintIds']) ? d['constraintIds'] : [],
  }),

  /* v5 → v6 : le Journal de bord apparaît. Une sauvegarde antérieure n'a pas
     mémorisé ce qui a été croisé — le journal repart donc vide et se remplira
     à la partie suivante. Inventer un historique serait pire : il annoncerait
     au joueur des appareils qu'il n'a peut-être jamais vus. */
  5: (d) => ({
    ...d,
    version: 6,
    seenEnemies: Array.isArray(d['seenEnemies']) ? d['seenEnemies'] : [],
  }),

  /* v4 → v5 : le choix d'arme secondaire apparaît. Une sauvegarde antérieure
     n'en a pas : on pose celle par défaut, la seule qu'elle ait jamais pu
     utiliser. */
  4: (d) => ({
    ...d,
    version: 5,
    secondaryId: typeof d['secondaryId'] === 'string' ? d['secondaryId'] : DEFAULT_SECONDARY_ID,
  }),

  3: (d) => {
    const best = typeof d['bestScore'] === 'number' ? (d['bestScore'] as number) : 0;
    return {
      ...d,
      version: 4,
      bestByDifficulty: Array.isArray(d['bestByDifficulty'])
        ? d['bestByDifficulty']
        : [0, best, 0],
      lastDifficulty: typeof d['lastDifficulty'] === 'number' ? d['lastDifficulty'] : 1,
    };
  },
};

/**
 * Applique les migrations nécessaires.
 *
 * ⚠️ Ne JAMAIS jeter une sauvegarde qu'on ne comprend pas. Une version
 * inconnue — plus récente que le code, ce qui arrive quand un joueur revient
 * sur un onglet en cache après une mise à jour — est fusionnée avec les
 * valeurs par défaut plutôt qu'effacée. Perdre la progression est bien pire
 * que fonctionner en mode dégradé.
 */
export function migrate(raw: unknown): SaveData {
  if (!raw || typeof raw !== 'object') return defaultSave();

  let data = { ...(raw as Record<string, unknown>) };
  let v = typeof data['version'] === 'number' ? (data['version'] as number) : 1;

  while (v < SAVE_VERSION) {
    const step = MIGRATIONS[v];
    if (!step) break; // saut de version inconnu : on complète, on n'efface pas
    data = step(data);
    const next = typeof data['version'] === 'number' ? (data['version'] as number) : v + 1;
    if (next <= v) break; // garde anti-boucle infinie
    v = next;
  }

  const base = defaultSave();
  return {
    ...base,
    ...data,
    version: SAVE_VERSION,
    options: { ...base.options, ...((data['options'] as object) ?? {}) },
    unlocked: Array.isArray(data['unlocked']) ? (data['unlocked'] as string[]) : base.unlocked,
    milestones: Array.isArray(data['milestones'])
      ? (data['milestones'] as string[])
      : base.milestones,
    // Longueur FORCÉE à trois : une sauvegarde tronquée ou bricolée donnerait
    // `undefined` là où l'interface promet un nombre, et l'écran de briefing
    // afficherait « NaN » sans que rien ne casse par ailleurs.
    bestByDifficulty: normalizeBests(data['bestByDifficulty']),
    seenEnemies: Array.isArray(data['seenEnemies'])
      ? (data['seenEnemies'] as string[])
      : base.seenEnemies,
    constraintIds: Array.isArray(data['constraintIds'])
      ? (data['constraintIds'] as string[])
      : base.constraintIds,
  };
}

/** Ramène le tableau des records à exactement trois entrées numériques. */
function normalizeBests(raw: unknown): number[] {
  const src = Array.isArray(raw) ? raw : [];
  const out: number[] = [];
  for (let i = 0; i < 3; i++) {
    const v = src[i];
    out.push(typeof v === 'number' && v >= 0 ? v : 0);
  }
  return out;
}

export class SaveStore {
  private readonly store: KeyValueStore;
  private cache: SaveData = defaultSave();
  private writing: Promise<void> | null = null;
  private pending: SaveData | null = null;

  constructor(store: KeyValueStore) {
    this.store = store;
  }

  get data(): SaveData {
    return this.cache;
  }

  async load(): Promise<SaveData> {
    try {
      this.cache = migrate(await this.store.get(SAVE_KEY));
    } catch {
      // Un stockage inaccessible — navigation privée, quota, permissions — ne
      // doit jamais empêcher de JOUER. On repart d'une sauvegarde neuve, en
      // mémoire seulement.
      this.cache = defaultSave();
    }
    return this.cache;
  }

  /**
   * Écriture coalescée : plusieurs sauvegardes rapprochées ne produisent qu'une
   * seule transaction. Sans ça, une fin de run qui met à jour l'alliage, le
   * meilleur score et le compteur déclencherait trois écritures concurrentes
   * sur la même clé — dont l'ordre d'arrivée n'est pas garanti.
   */
  async save(patch: Partial<SaveData>): Promise<void> {
    this.cache = { ...this.cache, ...patch, version: SAVE_VERSION };
    this.pending = this.cache;
    if (this.writing) return this.writing;

    this.writing = (async () => {
      while (this.pending) {
        const snapshot = this.pending;
        this.pending = null;
        try {
          await this.store.set(SAVE_KEY, snapshot);
        } catch {
          /* stockage indisponible : on continue de jouer en mémoire */
        }
      }
      this.writing = null;
    })();

    return this.writing;
  }

  /** Export lisible par un humain — le seul vrai filet contre la purge ITP de
   *  Safari, qui efface le stockage après sept jours d'inactivité. */
  export(): string {
    return JSON.stringify(this.cache, null, 2);
  }

  async import(json: string): Promise<boolean> {
    try {
      const parsed: unknown = JSON.parse(json);
      this.cache = migrate(parsed);
      await this.save({});
      return true;
    } catch {
      return false;
    }
  }
}

/** Demande la persistance durable. Échoue silencieusement si l'API n'existe
 *  pas — c'est un bonus, jamais un prérequis. */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
